import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useState, useCallback, useEffect, useRef, useMemo, } from 'react';
import { BANNER_DEFAULTS, } from './bannerTypes';
const BannerContext = createContext(undefined);
/**
 * Selects the highest priority eligible banner from submissions.
 *
 * Eligible: persistent always, transient only if not stale.
 * Selection order: highest priority → oldest timestamp → lexicographic id.
 */
function selectActiveBanner(submissions, now, staleMs) {
    let best = null;
    for (const banner of submissions.values()) {
        // Check eligibility for transient banners
        if (banner.kind === 'transient') {
            const submittedAt = banner.submittedAt ?? banner.registeredAt;
            if (now - submittedAt >= staleMs) {
                // Stale - not eligible
                continue;
            }
        }
        if (!best) {
            best = banner;
            continue;
        }
        // Compare priority (higher wins)
        if (banner.priority > best.priority) {
            best = banner;
            continue;
        }
        if (banner.priority < best.priority) {
            continue;
        }
        // Same priority - compare timestamps (older wins = FIFO)
        const bannerTime = banner.kind === 'transient' ? (banner.submittedAt ?? banner.registeredAt) : banner.registeredAt;
        const bestTime = best.kind === 'transient' ? (best.submittedAt ?? best.registeredAt) : best.registeredAt;
        if (bannerTime < bestTime) {
            best = banner;
            continue;
        }
        if (bannerTime > bestTime) {
            continue;
        }
        // Same timestamp - lexicographic id as tie-breaker
        if (banner.id < best.id) {
            best = banner;
        }
    }
    return best;
}
/**
 * Removes stale transient banners from submissions.
 * Returns true if any banners were removed.
 */
function removeStaleTransients(submissions, now, staleMs) {
    let removed = false;
    for (const [id, banner] of submissions.entries()) {
        if (banner.kind === 'transient') {
            const submittedAt = banner.submittedAt ?? banner.registeredAt;
            if (now - submittedAt >= staleMs) {
                submissions.delete(id);
                banner.onDismiss?.();
                removed = true;
            }
        }
    }
    return removed;
}
export function BannerProvider({ children, config = {} }) {
    const { staleMs = BANNER_DEFAULTS.STALE_MS, cleanupIntervalMs = BANNER_DEFAULTS.CLEANUP_INTERVAL_MS, getNow = Date.now, } = config;
    // Use ref for submissions to avoid re-renders on every change
    const submissionsRef = useRef(new Map());
    const [activeBanner, setActiveBanner] = useState(null);
    // Recompute active banner from current submissions
    const updateActiveBanner = useCallback(() => {
        const now = getNow();
        removeStaleTransients(submissionsRef.current, now, staleMs);
        const newActive = selectActiveBanner(submissionsRef.current, now, staleMs);
        setActiveBanner(newActive);
    }, [getNow, staleMs]);
    // Submit or update a banner
    const submit = useCallback((banner) => {
        const now = getNow();
        const internal = {
            ...banner,
            registeredAt: now,
        };
        submissionsRef.current.set(banner.id, internal);
        updateActiveBanner();
    }, [getNow, updateActiveBanner]);
    // Remove a banner by id
    const remove = useCallback((id) => {
        const banner = submissionsRef.current.get(id);
        if (banner) {
            submissionsRef.current.delete(id);
            banner.onDismiss?.();
            updateActiveBanner();
        }
    }, [updateActiveBanner]);
    // Dismiss currently active banner
    const dismissActive = useCallback(() => {
        if (!activeBanner)
            return;
        const isDismissible = activeBanner.dismissible ?? (activeBanner.kind === 'transient');
        if (!isDismissible) {
            // Persistent banner with dismissible: false - no-op
            return;
        }
        submissionsRef.current.delete(activeBanner.id);
        activeBanner.onDismiss?.();
        updateActiveBanner();
    }, [activeBanner, updateActiveBanner]);
    // Clear all banners (for tests)
    const __clearAll = useCallback(() => {
        for (const banner of submissionsRef.current.values()) {
            banner.onDismiss?.();
        }
        submissionsRef.current.clear();
        setActiveBanner(null);
    }, []);
    // Periodic cleanup of stale transients
    useEffect(() => {
        const interval = setInterval(() => {
            const now = getNow();
            const hadChanges = removeStaleTransients(submissionsRef.current, now, staleMs);
            if (hadChanges) {
                updateActiveBanner();
            }
        }, cleanupIntervalMs);
        return () => clearInterval(interval);
    }, [cleanupIntervalMs, getNow, staleMs, updateActiveBanner]);
    const contextValue = useMemo(() => ({
        activeBanner,
        submit,
        remove,
        dismissActive,
        __clearAll,
    }), [activeBanner, submit, remove, dismissActive, __clearAll]);
    return (_jsx(BannerContext.Provider, { value: contextValue, children: children }));
}
/**
 * Hook to access banner manager API and active banner state.
 */
export function useBannerContext() {
    const context = useContext(BannerContext);
    if (!context) {
        throw new Error('useBannerContext must be used within a BannerProvider');
    }
    return context;
}
//# sourceMappingURL=BannerProvider.js.map